import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Building2, Users, Activity as ActivityIcon,
  Handshake, Plus, ExternalLink, Mail, Phone, Linkedin,
  Edit2, Save, X as XIcon, TrendingUp, DollarSign, CalendarCheck,
  Loader2, ArrowUpRight, FileText, Percent, Clock, AlertTriangle,
  Link2,
} from "lucide-react";
import { API, useAuth } from "../../App";
import { ReferralUpdateAutomation } from "../../components/admin/ReferralUpdateAutomation";
import { ScorecardPanel, DocumentsSection, RemindersSection } from "./PartnerEngagementPanels";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/**
 * Partners CRM — Company detail (Phase 1).
 *
 * Loads a single company + inline stats, then tabs for Contacts, Activities,
 * and Referrals. Quick-log dialogs write directly to the corresponding
 * `/api/partners/*` endpoints and refetch each tab list on success.
 */

const REFERRAL_STATUS_TONE = {
  new: "bg-blue-100 text-blue-700",
  contacted: "bg-indigo-100 text-indigo-700",
  touring: "bg-amber-100 text-amber-700",
  signed: "bg-emerald-100 text-emerald-700",
  converted: "bg-emerald-100 text-emerald-700",
  lost: "bg-red-100 text-red-700",
};

const COMPANY_STATUS_TONE = {
  Prospect: "bg-slate-100 text-slate-700",
  Contacted: "bg-blue-100 text-blue-700",
  "Meeting Scheduled": "bg-indigo-100 text-indigo-700",
  Partner: "bg-emerald-100 text-emerald-700",
  Active: "bg-emerald-100 text-emerald-700",
  Dormant: "bg-amber-100 text-amber-700",
  Inactive: "bg-red-100 text-red-700",
};

let _enumsCache = null; // module-level: enums are static per session


export default function PartnerCompanyDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const { toast } = useToast();
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [company, setCompany] = useState(null);
  const [enums, setEnums] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, e, ct, ac, rf, perf, sc, docs, rem] = await Promise.all([
        axios.get(`${API}/partners/companies/${id}`, { headers: authHeader }),
        _enumsCache ? Promise.resolve({ data: _enumsCache }) : axios.get(`${API}/partners/enums`, { headers: authHeader }),
        axios.get(`${API}/partners/contacts`, { headers: authHeader, params: { company_id: id, limit: 200 } }),
        axios.get(`${API}/partners/activities`, { headers: authHeader, params: { company_id: id, limit: 100 } }),
        axios.get(`${API}/partners/referrals`, { headers: authHeader, params: { company_id: id, limit: 100 } }),
        axios.get(`${API}/partners/companies/${id}/performance`, { headers: authHeader }),
        axios.get(`${API}/partners/companies/${id}/scorecard`, { headers: authHeader }),
        axios.get(`${API}/partners/documents`, { headers: authHeader, params: { company_id: id } }),
        axios.get(`${API}/partners/reminders`, { headers: authHeader, params: { company_id: id, limit: 100 } }),
      ]);
      setCompany(c.data);
      setEditForm(c.data);
      _enumsCache = e.data;
      setEnums(e.data);
      setContacts(ct.data.items || []);
      setActivities(ac.data.items || []);
      setReferrals(rf.data.items || []);
      setPerformance(perf.data);
      setScorecard(sc.data);
      setDocuments(docs.data.items || []);
      setReminders(rem.data.items || []);
    } catch (err) {
      toast({ title: "Failed to load", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
     
  }, [id, authHeader, toast]);

  useEffect(() => { loadAll(); }, [loadAll]);


  const saveCompany = async () => {
    try {
      const payload = { ...editForm };
      delete payload._stats;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      if (payload.employee_count === "") delete payload.employee_count;
      else if (payload.employee_count != null) payload.employee_count = parseInt(payload.employee_count, 10);
      await axios.put(`${API}/partners/companies/${id}`, payload, { headers: authHeader });
      toast({ title: "Company updated" });
      setEditMode(false);
      loadAll();
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    }
  };

  if (loading && !company) {
    return <div className="p-8 text-slate-400" data-testid="partner-detail-loading">Loading company…</div>;
  }
  if (!company) {
    return <div className="p-8 text-slate-400">Company not found.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="partner-company-detail-page">
      <Link
        to="/admin/partners"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#DE6E4B] mb-4"
        data-testid="partner-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Companies
      </Link>

      {/* Company header card */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#DE6E4B]" />
            </div>
            <div>
              {editMode ? (
                <Input
                  className="text-xl font-bold"
                  value={editForm.company_name || ""}
                  onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                />
              ) : (
                <h1 className="text-2xl font-bold text-slate-900">{company.company_name}</h1>
              )}
              <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                {company.industry && <span>{company.industry}</span>}
                {company.industry && company.partner_type && <span>·</span>}
                {company.partner_type && <span>{company.partner_type}</span>}
                {company.houston_submarket && (<><span>·</span><span>{company.houston_submarket}</span></>)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editMode && (
              <Badge
                variant="outline"
                className={`${COMPANY_STATUS_TONE[company.company_status] || "bg-slate-100 text-slate-700"} border-transparent`}
              >
                {company.company_status}
              </Badge>
            )}
            {editMode ? (
              <>
                <Button size="sm" onClick={saveCompany} className="bg-[#DE6E4B] hover:bg-[#c85d3f]" data-testid="partner-save-btn">
                  <Save className="w-4 h-4 mr-1" /> Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditMode(false); setEditForm(company); }}>
                  <XIcon className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditMode(true)} data-testid="partner-edit-btn">
                  <Edit2 className="w-4 h-4 mr-1" /> Edit
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Edit form */}
        {editMode && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-slate-100">
            <div>
              <Label>Industry</Label>
              <Select value={editForm.industry || ""} onValueChange={(v) => setEditForm({ ...editForm, industry: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(enums?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Partner type</Label>
              <Select value={editForm.partner_type || ""} onValueChange={(v) => setEditForm({ ...editForm, partner_type: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(enums?.partner_types || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.company_status || ""} onValueChange={(v) => setEditForm({ ...editForm, company_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(enums?.company_statuses || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Submarket</Label>
              <Select value={editForm.houston_submarket || ""} onValueChange={(v) => setEditForm({ ...editForm, houston_submarket: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(enums?.houston_submarkets || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={editForm.website || ""} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
            </div>
            <div>
              <Label>Employee count</Label>
              <Input
                type="number"
                value={editForm.employee_count ?? ""}
                onChange={(e) => setEditForm({ ...editForm, employee_count: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={3} value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
          </div>
        )}

        {!editMode && company.notes && (
          <div className="text-sm text-slate-600 mt-2 whitespace-pre-line">{company.notes}</div>
        )}

        {!editMode && company.website && (
          <a href={company.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 mt-2">
            {company.website} <ExternalLink className="w-3 h-3" />
          </a>
        )}

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <MiniStat label="Contacts" value={company._stats?.contacts ?? contacts.length} icon={Users} />
          <MiniStat label="Activities" value={company._stats?.activities ?? activities.length} icon={ActivityIcon} />
          <MiniStat label="Referrals" value={company._stats?.referrals ?? referrals.length} icon={Handshake} />
          <MiniStat label="Leads" value={company._stats?.leads ?? 0} icon={Users} />
        </div>
      </div>

      {/* Partner Performance panel (Phase 2) */}
      <PartnerPerformancePanel performance={performance} />

      {/* Relationship Scorecard (Phase 3) */}
      <ScorecardPanel scorecard={scorecard} />

      {/* Tabs */}
      <div className="mb-6"><ReferralUpdateAutomation companyId={id} /></div>

      <Tabs defaultValue="contacts">
        <TabsList className="mb-4">
          <TabsTrigger value="contacts" data-testid="partner-tab-contacts">Contacts ({contacts.length})</TabsTrigger>
          <TabsTrigger value="activities" data-testid="partner-tab-activities">Activities ({activities.length})</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="partner-tab-referrals">Referrals ({referrals.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="partner-tab-documents">Documents ({documents.length})</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="partner-tab-reminders">
            Reminders ({reminders.filter((r) => r.status === "pending").length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contacts">
          <ContactsSection
            companyId={id}
            contacts={contacts}
            enums={enums}
            authHeader={authHeader}
            onReload={loadAll}
          />
        </TabsContent>

        <TabsContent value="activities">
          <ActivitiesSection
            contacts={contacts}
            activities={activities}
            enums={enums}
            authHeader={authHeader}
            onReload={loadAll}
            companyId={id}
          />
        </TabsContent>

        <TabsContent value="referrals">
          <ReferralsSection
            companyId={id}
            contacts={contacts}
            enums={enums}
            referrals={referrals}
            authHeader={authHeader}
            onReload={loadAll}
          />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsSection
            companyId={id}
            documents={documents}
            authHeader={authHeader}
            onReload={loadAll}
          />
        </TabsContent>

        <TabsContent value="reminders">
          <RemindersSection
            companyId={id}
            contacts={contacts}
            reminders={reminders}
            authHeader={authHeader}
            onReload={loadAll}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
      <Icon className="w-5 h-5 text-slate-400" />
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-lg font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

// ── Contacts ────────────────────────────────────────────────────────────
function ContactsSection({ companyId, contacts, enums, authHeader, onReload }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // contact being edited, or null
  const [form, setForm] = useState({
    first_name: "", last_name: "", title: "", email: "", phone: "",
    contact_type: "", pipeline_stage: "Research", decision_maker: false, notes: "",
  });
  const [perfContact, setPerfContact] = useState(null);  // contact whose performance dialog is open
  const [perf, setPerf] = useState(null);

  const openPerformance = async (c) => {
    setPerfContact(c);
    setPerf(null);
    try {
      const res = await axios.get(`${API}/partners/contacts/${c.id}/performance`, { headers: authHeader });
      setPerf(res.data);
    } catch (err) {
      toast({ title: "Failed to load performance", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    }
  };

  const copyPortalLink = async (e, c) => {
    e.stopPropagation();
    try {
      const res = await axios.post(`${API}/partners/contacts/${c.id}/portal-link`, {}, { headers: authHeader });
      const url = res.data.url;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked — still show url */ }
      toast({ title: "Portal link copied", description: url });
    } catch (err) {
      toast({ title: "Failed to get portal link", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    }
  };

  const emptyForm = { first_name: "", last_name: "", title: "", email: "", phone: "", contact_type: "", pipeline_stage: "Research", decision_maker: false, notes: "" };

  const startEdit = (e, c) => {
    e.stopPropagation();
    setEditing(c);
    setForm({
      first_name: c.first_name || "", last_name: c.last_name || "", title: c.title || "",
      email: c.email || "", phone: c.phone || "", contact_type: c.contact_type || "",
      pipeline_stage: c.pipeline_stage || "Research", decision_maker: !!c.decision_maker,
      notes: c.notes || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.first_name.trim()) {
      toast({ title: "First name required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.email) delete payload.email; // EmailStr rejects ""
        await axios.put(`${API}/partners/contacts/${editing.id}`, payload, { headers: authHeader });
        toast({ title: "Contact updated" });
      } else {
        const payload = { company_id: companyId, ...form };
        Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
        await axios.post(`${API}/partners/contacts`, payload, { headers: authHeader });
        toast({ title: "Contact added" });
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      onReload();
    } catch (err) {
      toast({ title: editing ? "Update failed" : "Create failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Contacts</h3>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#DE6E4B] hover:bg-[#c85d3f]" data-testid="partner-add-contact-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="partner-contact-dialog">
            <DialogHeader><DialogTitle>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="partner-contact-firstname" /></div>
                <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              </div>
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Contact type</Label>
                  <Select value={form.contact_type} onValueChange={(v) => setForm({ ...form, contact_type: v })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{(enums?.contact_types || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pipeline stage</Label>
                  <Select value={form.pipeline_stage} onValueChange={(v) => setForm({ ...form, pipeline_stage: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(enums?.pipeline_stages || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#DE6E4B] hover:bg-[#c85d3f]" onClick={submit} disabled={saving} data-testid="partner-contact-submit">
                {saving ? "Saving…" : editing ? "Save Changes" : "Create Contact"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {contacts.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm">No contacts yet.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {contacts.map((c) => (
            <li
              key={c.id}
              className="p-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
              onClick={() => openPerformance(c)}
              data-testid={`partner-contact-row-${c.id}`}
            >
              <div>
                <div className="font-semibold text-slate-900">
                  {c.first_name} {c.last_name}
                  {c.decision_maker && <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Decision Maker</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {c.title || "—"}{c.contact_type ? ` · ${c.contact_type}` : ""}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                  {c.linkedin && <span className="inline-flex items-center gap-1"><Linkedin className="w-3 h-3" /> LinkedIn</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-400 hover:text-[#0A2342]"
                  onClick={(e) => startEdit(e, c)}
                  title="Edit contact"
                  data-testid={`partner-contact-edit-${c.id}`}
                >
                  <Edit2 className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-slate-400 hover:text-[#DE6E4B]"
                  onClick={(e) => copyPortalLink(e, c)}
                  title="Copy this contact's secure Partner Hub link"
                  data-testid={`partner-contact-portal-link-${c.id}`}
                >
                  <Link2 className="w-4 h-4 mr-1" /> Portal
                </Button>
                <Badge variant="outline">{c.pipeline_stage}</Badge>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Contact Performance dialog (Phase 2) */}
      <Dialog open={!!perfContact} onOpenChange={(o) => { if (!o) { setPerfContact(null); setPerf(null); } }}>
        <DialogContent className="max-w-md" data-testid="contact-performance-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#DE6E4B]" />
              {perfContact ? `${perfContact.first_name} ${perfContact.last_name || ""}`.trim() : ""} — Performance
            </DialogTitle>
          </DialogHeader>
          {!perf ? (
            <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="contact-performance-metrics">
              <PerfCard icon={Handshake} label="Referrals Submitted" value={perf.referrals_submitted} />
              <PerfCard icon={Users} label="Converted Leads" value={perf.converted_leads} />
              <PerfCard icon={FileText} label="Leases Closed" value={perf.leases_closed} highlight />
              <PerfCard icon={DollarSign} label="Commission" value={`$${(perf.commission_generated || 0).toLocaleString()}`} highlight />
              <PerfCard icon={Clock} label="Last Referral" value={perf.last_referral ? new Date(perf.last_referral).toLocaleDateString() : "—"} />
              <PerfCard icon={CalendarCheck} label="Next Follow-up" value={perf.next_follow_up ? new Date(perf.next_follow_up).toLocaleDateString() : "—"} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Activities ──────────────────────────────────────────────────────────
function ActivitiesSection({ companyId, contacts, activities, enums, authHeader, onReload }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    contact_id: "", activity_type: "Email", description: "", outcome: "", next_action: "",
  });

  const submit = async () => {
    if (!form.contact_id) {
      toast({ title: "Select a contact", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = { company_id: companyId, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/partners/activities`, payload, { headers: authHeader });
      toast({ title: "Activity logged" });
      setOpen(false);
      setForm({ contact_id: "", activity_type: "Email", description: "", outcome: "", next_action: "" });
      onReload();
    } catch (err) {
      toast({ title: "Log failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const contactName = (cid) => {
    const c = contacts.find((x) => x.id === cid);
    return c ? `${c.first_name} ${c.last_name}` : "—";
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Activity Timeline</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#DE6E4B] hover:bg-[#c85d3f]" disabled={contacts.length === 0} data-testid="partner-add-activity-btn">
              <Plus className="w-4 h-4 mr-1" /> Log Activity
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="partner-activity-dialog">
            <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Contact *</Label>
                <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                  <SelectTrigger data-testid="partner-activity-contact"><SelectValue placeholder="Choose a contact" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(enums?.activity_types || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Outcome</Label><Input value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} /></div>
              <div><Label>Next action</Label><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#DE6E4B] hover:bg-[#c85d3f]" onClick={submit} disabled={saving} data-testid="partner-activity-submit">
                {saving ? "Saving…" : "Log Activity"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {contacts.length === 0 && (
        <div className="p-4 text-xs text-amber-700 bg-amber-50 border-b border-amber-200">
          Add a contact first before logging activities.
        </div>
      )}
      {activities.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm">No activities logged yet.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {activities.map((a) => (
            <li key={a.id} className="p-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{a.activity_type}</Badge>
                <span className="font-medium text-slate-800 text-sm">{contactName(a.contact_id)}</span>
                <span className="text-xs text-slate-400 ml-auto">
                  {a.activity_date ? new Date(a.activity_date).toLocaleString() : ""}
                </span>
              </div>
              {a.description && <div className="text-sm text-slate-600 mt-1">{a.description}</div>}
              {(a.outcome || a.next_action) && (
                <div className="text-xs text-slate-500 mt-1 space-x-2">
                  {a.outcome && <span><b>Outcome:</b> {a.outcome}</span>}
                  {a.next_action && <span><b>Next:</b> {a.next_action}</span>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Referrals ───────────────────────────────────────────────────────────
function ReferralsSection({ companyId, contacts, referrals, authHeader, onReload }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(null);   // referral_id being converted
  const [preview, setPreview] = useState(null);          // { referralId, referral, existingLead } — merge modal
  const [merging, setMerging] = useState(false);
  const [form, setForm] = useState({
    referral_name: "", referral_email: "", referral_phone: "",
    contact_id: "", budget: "", bedrooms: "", bathrooms: "", pets: "",
    apartment_preferences: "", preferred_neighborhood: "", move_date: "",
  });

  const submit = async () => {
    if (!form.referral_name.trim()) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = { company_id: companyId, ...form };
      if (payload.budget) payload.budget = parseFloat(payload.budget);
      else delete payload.budget;
      if (payload.pets === "yes") payload.pets = true;
      else if (payload.pets === "no") payload.pets = false;
      else delete payload.pets;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/partners/referrals`, payload, { headers: authHeader });
      toast({ title: "Referral added" });
      setOpen(false);
      setForm({ referral_name: "", referral_email: "", referral_phone: "", contact_id: "", budget: "", bedrooms: "", bathrooms: "", pets: "", apartment_preferences: "", preferred_neighborhood: "", move_date: "" });
      onReload();
    } catch (err) {
      toast({ title: "Add failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const doConvert = async (referralId, mergeConfirmed) => {
    try {
      const res = await axios.post(
        `${API}/partners/referrals/${referralId}/convert`,
        {},
        { headers: authHeader, params: { merge_confirmed: mergeConfirmed } },
      );
      const { was_existing, already_converted } = res.data;
      toast({
        title: already_converted ? "Already converted" : (was_existing ? "Merged into existing lead" : "Lead created"),
        description: was_existing
          ? "The referral was merged — corporate partner, contact, and referral are now cross-linked on the existing lead."
          : "The lead is enrolled in the D0–D14 drip cadence.",
      });
      setPreview(null);
      onReload();
    } catch (err) {
      if (err?.response?.status === 409) {
        const d = err.response.data?.detail;
        setPreview({ referralId, existingLead: d?.existing_lead || null });
        return;
      }
      toast({ title: "Convert failed", description: err?.response?.data?.detail?.message || err?.response?.data?.detail || err.message, variant: "destructive" });
    }
  };

  const convertReferral = async (referralId) => {
    setConverting(referralId);
    try {
      const pv = await axios.get(`${API}/partners/referrals/${referralId}/convert-preview`, { headers: authHeader });
      if (pv.data.error) {
        toast({ title: "Cannot convert", description: pv.data.error, variant: "destructive" });
        return;
      }
      if (pv.data.would_merge && pv.data.existing_lead) {
        setPreview({ referralId, referral: pv.data.referral, existingLead: pv.data.existing_lead });
        return;
      }
      await doConvert(referralId, false);
    } catch (err) {
      toast({ title: "Convert failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally { setConverting(null); }
  };

  const confirmMerge = async () => {
    if (!preview) return;
    setMerging(true);
    await doConvert(preview.referralId, true);
    setMerging(false);
  };

  const contactName = (cid) => {
    const c = contacts.find((x) => x.id === cid);
    return c ? `${c.first_name} ${c.last_name}` : "—";
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Referrals</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#DE6E4B] hover:bg-[#c85d3f]" data-testid="partner-add-referral-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Referral
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="partner-referral-dialog">
            <DialogHeader><DialogTitle>Add Referral</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Referral name *</Label><Input value={form.referral_name} onChange={(e) => setForm({ ...form, referral_name: e.target.value })} data-testid="partner-referral-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email <span className="text-xs text-slate-400">(needed for conversion)</span></Label>
                  <Input type="email" value={form.referral_email} onChange={(e) => setForm({ ...form, referral_email: e.target.value })} data-testid="partner-referral-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.referral_phone} onChange={(e) => setForm({ ...form, referral_phone: e.target.value })} data-testid="partner-referral-phone" />
                </div>
              </div>
              <div>
                <Label>Referring contact</Label>
                <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Budget ($/mo)</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
                <div><Label>Bedrooms</Label><Input value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} placeholder="1BR, 2BR…" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Bathrooms</Label><Input value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} placeholder="1, 1.5, 2…" data-testid="partner-referral-bathrooms" /></div>
                <div>
                  <Label>Pets</Label>
                  <Select value={form.pets} onValueChange={(v) => setForm({ ...form, pets: v })}>
                    <SelectTrigger data-testid="partner-referral-pets"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Preferred neighborhood</Label><Input value={form.preferred_neighborhood} onChange={(e) => setForm({ ...form, preferred_neighborhood: e.target.value })} /></div>
              <div><Label>Apartment preferences</Label><Textarea rows={2} value={form.apartment_preferences} onChange={(e) => setForm({ ...form, apartment_preferences: e.target.value })} placeholder="Pool, gated, EV charging…" data-testid="partner-referral-preferences" /></div>
              <div><Label>Move-in date</Label><Input type="date" value={form.move_date} onChange={(e) => setForm({ ...form, move_date: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-[#DE6E4B] hover:bg-[#c85d3f]" onClick={submit} disabled={saving} data-testid="partner-referral-submit">
                {saving ? "Saving…" : "Add Referral"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {referrals.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-sm">No referrals yet.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {referrals.map((r) => {
            const isConverted = r.referral_status === "converted" && r.lead_id;
            const isConverting = converting === r.id;
            return (
              <li key={r.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">
                    {r.referral_name}
                    {r.referral_email && <span className="ml-2 text-xs text-slate-400 font-normal">{r.referral_email}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Referred by {contactName(r.contact_id)}
                    {r.budget ? ` · $${r.budget}/mo` : ""}
                    {r.bedrooms ? ` · ${r.bedrooms}` : ""}
                    {r.preferred_neighborhood ? ` · ${r.preferred_neighborhood}` : ""}
                  </div>
                  {isConverted && r.converted_at && (
                    <div className="text-xs text-emerald-700 mt-1">
                      Converted {new Date(r.converted_at).toLocaleDateString()}
                      {r.converted_by ? ` by ${r.converted_by}` : ""}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.commission ? <span className="text-xs text-emerald-700 font-semibold">${r.commission}</span> : null}
                  <Badge
                    variant="outline"
                    className={`${REFERRAL_STATUS_TONE[r.referral_status] || "bg-slate-100 text-slate-700"} border-transparent`}
                  >
                    {r.referral_status}
                  </Badge>
                  {isConverted ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/leads?lead=${r.lead_id}`)}
                      data-testid={`partner-open-lead-${r.id}`}
                    >
                      Open Lead <ArrowUpRight className="w-3 h-3 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-[#DE6E4B] hover:bg-[#c85d3f]"
                      onClick={() => convertReferral(r.id)}
                      disabled={isConverting || !r.referral_email}
                      title={!r.referral_email ? "Add an email to this referral before converting" : "Create a Lead from this referral"}
                      data-testid={`partner-convert-referral-${r.id}`}
                    >
                      {isConverting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <>Convert to Lead <ArrowUpRight className="w-3 h-3 ml-1" /></>
                      )}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Convert preview — duplicate lead detected (Merge / Open Existing / Cancel) */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-lg" data-testid="convert-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Duplicate Lead Detected
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            A lead with this email or phone already exists in the Lead CRM. You can
            <b> merge</b> the referral into it (linking the corporate partner, contact, and referral),
            open the existing lead, or cancel.
          </p>
          {preview?.existingLead && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="convert-existing-lead-card">
              <div className="font-semibold text-slate-900">{preview.existingLead.name || "Unnamed lead"}</div>
              <div className="text-slate-600 mt-1 space-y-0.5">
                {preview.existingLead.email && <div>Email: {preview.existingLead.email}</div>}
                {preview.existingLead.phone && <div>Phone: {preview.existingLead.phone}</div>}
                <div>
                  Status: <span className="capitalize">{preview.existingLead.status || "—"}</span>
                  {preview.existingLead.stage ? <> · Stage: <span className="capitalize">{preview.existingLead.stage}</span></> : null}
                </div>
                {preview.existingLead.created_at && (
                  <div>Created: {new Date(preview.existingLead.created_at).toLocaleDateString()}</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="convert-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/leads?lead=${preview?.existingLead?.id}`)}
              disabled={!preview?.existingLead?.id}
              data-testid="convert-open-existing-btn"
            >
              Open Existing Lead <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
            <Button
              className="bg-[#DE6E4B] hover:bg-[#c85d3f]"
              onClick={confirmMerge}
              disabled={merging}
              data-testid="convert-merge-btn"
            >
              {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : "Merge & Convert"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Partner Performance Panel (Phase 2) ─────────────────────────────────
function PartnerPerformancePanel({ performance }) {
  if (!performance) return null;
  const {
    total_referrals, active_leads, tours_scheduled, applications_submitted,
    leases_signed, conversion_rate, commission_earned, avg_days_to_lease,
    last_referral_date,
  } = performance;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6" data-testid="partner-performance-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#DE6E4B]" />
          <h3 className="font-semibold text-slate-900">Partner Performance</h3>
        </div>
        {last_referral_date && (
          <span className="text-xs text-slate-500">
            Last referral: {new Date(last_referral_date).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
        <PerfCard icon={Handshake} label="Total Referrals" value={total_referrals} />
        <PerfCard icon={Users} label="Active Leads" value={active_leads} />
        <PerfCard icon={CalendarCheck} label="Tours Scheduled" value={tours_scheduled} />
        <PerfCard icon={FileText} label="Applications" value={applications_submitted} />
        <PerfCard icon={DollarSign} label="Leases Signed" value={leases_signed} highlight />
        <PerfCard icon={Percent} label="Conversion Rate" value={`${conversion_rate}%`} />
        <PerfCard icon={DollarSign} label="Commission" value={`$${(commission_earned || 0).toLocaleString()}`} highlight />
        <PerfCard icon={Clock} label="Avg Days to Lease" value={avg_days_to_lease != null ? `${avg_days_to_lease}d` : "—"} />
      </div>
    </div>
  );
}

function PerfCard({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 border ${highlight ? "bg-orange-50 border-orange-100" : "bg-slate-50 border-slate-100"}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
        <Icon className={`w-3.5 h-3.5 ${highlight ? "text-[#DE6E4B]" : ""}`} />
        {label}
      </div>
      <div className={`text-xl font-bold mt-1 ${highlight ? "text-[#DE6E4B]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
